import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import { ChevronLeft, ChevronRight, Save } from "@mui/icons-material";
import {
  useGetLabourWorkersQuery,
  useGetLabourAttendanceQuery,
  useBatchSaveAttendanceMutation,
  LabourWorker,
  AttendanceBatchRow,
} from "../../../redux/labourTrackerApi";
import { useGetComsBlocksQuery, ComsBlock } from "../../../redux/comsApi";

const NAVY = "#123456";

// ─── Cycle helpers ──────────────────────────────────────────────────────────
function currentCycle(anchor?: Date) {
  const now = anchor ?? new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const d = now.getDate();
  let sm = m;
  let sy = y;
  if (d < 17) {
    sm = m - 1;
    if (sm < 0) {
      sm = 11;
      sy--;
    }
  }
  const start = new Date(sy, sm, 17);
  const end = new Date(sm === 11 ? sy + 1 : sy, sm === 11 ? 0 : sm + 1, 16);
  return { start, end };
}

function fmtDate(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(d.getDate()).padStart(2, "0")}`;
}

function eachDay(start: Date, end: Date): Date[] {
  const days: Date[] = [];
  const cur = new Date(start);
  while (cur <= end) {
    days.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return days;
}

function fmtCycle(c: { start: Date; end: Date }) {
  return `${c.start.getDate()} ${c.start.toLocaleString("en", {
    month: "short",
  })} – ${c.end.getDate()} ${c.end.toLocaleString("en", {
    month: "short",
    year: "numeric",
  })}`;
}

// ─── Types ───────────────────────────────────────────────────────────────────
type Cell = { status: "present" | "absent"; blockId: string | null };

const CELL_SIZE = 32;

// ─── Component ───────────────────────────────────────────────────────────────
export const LabourRegister: React.FC = () => {
  const [anchorDate, setAnchorDate] = useState(() => currentCycle().start);
  const cycle = useMemo(() => currentCycle(anchorDate), [anchorDate]);
  const cycleStart = fmtDate(cycle.start);
  const cycleEnd = fmtDate(cycle.end);
  const days = useMemo(() => eachDay(cycle.start, cycle.end), [cycle]);

  const { data: blocks = [] } = useGetComsBlocksQuery();
  const { data: workers = [], isLoading: wLoading } = useGetLabourWorkersQuery({
    active: true,
  });
  const { data: existing = [], isLoading: aLoading } =
    useGetLabourAttendanceQuery({ from: cycleStart, to: cycleEnd });
  const [batchSave, { isLoading: saving }] = useBatchSaveAttendanceMutation();

  const [grid, setGrid] = useState<Record<string, Record<string, Cell>>>({});
  const [dirty, setDirty] = useState<Set<string>>(new Set());
  const gridRef = useRef(grid);
  useEffect(() => {
    gridRef.current = grid;
  }, [grid]);

  // ── Initialise grid from server data ──────────────────────────────────────
  useEffect(() => {
    const next: Record<string, Record<string, Cell>> = {};
    existing.forEach((a) => {
      if (!next[a.workerId]) next[a.workerId] = {};
      next[a.workerId][a.workDate] = {
        status: a.status as "present" | "absent",
        blockId: a.blockId,
      };
    });
    setGrid(next);
    setDirty(new Set());
  }, [existing]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const getCell = useCallback(
    (wid: string, ds: string): Cell =>
      grid[wid]?.[ds] ?? { status: "absent", blockId: null },
    [grid],
  );

  const toggleCell = useCallback(
    (wid: string, ds: string, defaultBlockId?: string) => {
      setGrid((g) => {
        const cur: Cell = g[wid]?.[ds] ?? {
          status: "absent",
          blockId: defaultBlockId ?? null,
        };
        const next: Record<string, Record<string, Cell>> = {
          ...g,
          [wid]: {
            ...g[wid],
            [ds]: {
              status: (cur.status === "present"
                ? "absent"
                : "present") as Cell["status"],
              blockId: cur.blockId ?? defaultBlockId ?? blocks[0]?.id ?? null,
            },
          },
        };
        return next;
      });
      setDirty((d) => new Set(d).add(`${wid}|${ds}`));
    },
    [blocks],
  );

  const markAllPresent = (wid: string, defaultBlockId?: string) => {
    setGrid((g) => {
      const cols: Record<string, Cell> = { ...g[wid] };
      days.forEach((d) => {
        const ds = fmtDate(d);
        const cur = cols[ds] ?? { status: "absent", blockId: null };
        cols[ds] = {
          status: "present",
          blockId: cur.blockId ?? defaultBlockId ?? blocks[0]?.id ?? null,
        };
      });
      return { ...g, [wid]: cols };
    });
    days.forEach((d) => {
      const ds = fmtDate(d);
      setDirty((prev) => new Set(prev).add(`${wid}|${ds}`));
    });
  };

  const markAllAbsent = (wid: string) => {
    setGrid((g) => {
      const cols: Record<string, Cell> = { ...g[wid] };
      days.forEach((d) => {
        const ds = fmtDate(d);
        cols[ds] = { ...(cols[ds] ?? { blockId: null }), status: "absent" };
      });
      return { ...g, [wid]: cols };
    });
    days.forEach((d) => {
      const ds = fmtDate(d);
      setDirty((prev) => new Set(prev).add(`${wid}|${ds}`));
    });
  };

  const handleSave = async () => {
    if (!dirty.size) return;
    const rows: AttendanceBatchRow[] = [];
    dirty.forEach((key) => {
      const [wid, ds] = key.split("|");
      const cell = gridRef.current[wid]?.[ds];
      if (!cell) return;
      const worker = workers.find((w) => w.id === wid);
      const blockId = cell.blockId ?? worker?.defaultBlockId ?? blocks[0]?.id;
      if (!blockId) return;
      rows.push({ workerId: wid, blockId, workDate: ds, status: cell.status });
    });
    if (!rows.length) return;
    await batchSave({ rows });
    setDirty(new Set());
  };

  // Autosave 1.5s after last change
  useEffect(() => {
    if (!dirty.size) return;
    const t = setTimeout(() => {
      handleSave();
    }, 1500);
    return () => clearTimeout(t);
  }, [dirty]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Grouping ──────────────────────────────────────────────────────────────
  const roleRank = (role: string) =>
    role === "Supervisor" ? 0 : role === "Artisan" ? 1 : 2;
  const blockMap = new Map<string, ComsBlock>(blocks.map((b) => [b.id!, b]));

  const groups = useMemo(() => {
    const byBlock = new Map<string | null, LabourWorker[]>();
    workers.forEach((w) => {
      const k = w.defaultBlockId ?? null;
      if (!byBlock.has(k)) byBlock.set(k, []);
      byBlock.get(k)!.push(w);
    });
    byBlock.forEach((arr) =>
      arr.sort(
        (a, b) =>
          roleRank(a.role) - roleRank(b.role) ||
          (a.displayOrder ?? 9999) - (b.displayOrder ?? 9999),
      ),
    );
    const out: { block: ComsBlock | null; rows: LabourWorker[] }[] = [];
    blocks.forEach((b) => {
      if (byBlock.has(b.id!)) out.push({ block: b, rows: byBlock.get(b.id!)! });
    });
    if (byBlock.has(null)) out.push({ block: null, rows: byBlock.get(null)! });
    return out;
  }, [workers, blocks]);

  // ── Row / col totals ──────────────────────────────────────────────────────
  const rowTotals = useMemo(() => {
    const t: Record<string, { days: number; cost: number }> = {};
    workers.forEach((w) => {
      const count = days.filter(
        (d) => grid[w.id!]?.[fmtDate(d)]?.status === "present",
      ).length;
      const cost =
        w.role === "Supervisor"
          ? count > 0
            ? Number(w.rate)
            : 0
          : count * Number(w.rate);
      t[w.id!] = { days: count, cost };
    });
    return t;
  }, [grid, workers, days]);

  const colTotals = useMemo(() => {
    const t: Record<string, number> = {};
    days.forEach((d) => {
      const ds = fmtDate(d);
      t[ds] = workers.filter(
        (w) => grid[w.id!]?.[ds]?.status === "present",
      ).length;
    });
    return t;
  }, [grid, workers, days]);

  // ── Block override per cell ───────────────────────────────────────────────
  const [cellBlockEdit, setCellBlockEdit] = useState<{
    wid: string;
    ds: string;
  } | null>(null);

  const setCellBlock = (wid: string, ds: string, blockId: string) => {
    setGrid((g) => ({
      ...g,
      [wid]: {
        ...g[wid],
        [ds]: { ...(g[wid]?.[ds] ?? { status: "absent" }), blockId },
      },
    }));
    setDirty((d) => new Set(d).add(`${wid}|${ds}`));
    setCellBlockEdit(null);
  };

  if (wLoading || aLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  const today = fmtDate(new Date());

  return (
    <Box>
      {/* Header row */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Attendance Register
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Cycle: {fmtCycle(cycle)}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Tooltip title="Previous cycle">
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={() =>
                  setAnchorDate((a) => {
                    const p = new Date(a);
                    p.setDate(p.getDate() - 30);
                    return currentCycle(p).start;
                  })
                }
                sx={{ minWidth: 36, p: 0.5 }}
              >
                <ChevronLeft />
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setAnchorDate(currentCycle().start)}
          >
            Current cycle
          </Button>
          <Tooltip title="Next cycle">
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={() =>
                  setAnchorDate((a) => {
                    const n = new Date(a);
                    n.setDate(n.getDate() + 32);
                    return currentCycle(n).start;
                  })
                }
                sx={{ minWidth: 36, p: 0.5 }}
              >
                <ChevronRight />
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            size="small"
            startIcon={
              saving ? <CircularProgress size={14} color="inherit" /> : <Save />
            }
            disabled={!dirty.size || saving}
            onClick={handleSave}
            sx={{ bgcolor: NAVY, "&:hover": { bgcolor: "#1a4a7a" }, ml: 1 }}
          >
            {saving ? "Saving…" : `Save${dirty.size ? ` (${dirty.size})` : ""}`}
          </Button>
        </Box>
      </Box>

      {dirty.size > 0 && (
        <Box mb={1}>
          <Typography variant="caption" color="warning.main">
            ● Unsaved changes — will autosave in a moment
          </Typography>
        </Box>
      )}

      {/* Grid */}
      <Card>
        <CardContent sx={{ p: 0, overflowX: "auto" }}>
          <Box
            component="table"
            sx={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }}
          >
            {/* HEADER */}
            <Box component="thead">
              <Box component="tr">
                <Box
                  component="th"
                  sx={{
                    textAlign: "left",
                    px: 2,
                    py: 1,
                    bgcolor: NAVY,
                    color: "#fff",
                    whiteSpace: "nowrap",
                    position: "sticky",
                    left: 0,
                    zIndex: 2,
                    minWidth: 160,
                  }}
                >
                  Worker
                </Box>
                {days.map((d) => {
                  const ds = fmtDate(d);
                  const isToday = ds === today;
                  const isWknd = d.getDay() === 0 || d.getDay() === 6;
                  return (
                    <Box
                      key={ds}
                      component="th"
                      sx={{
                        width: CELL_SIZE,
                        minWidth: CELL_SIZE,
                        textAlign: "center",
                        py: 0.5,
                        bgcolor: isToday ? "#1a4a7a" : NAVY,
                        color: isToday
                          ? "#ffd54f"
                          : isWknd
                          ? "#90caf9"
                          : "#fff",
                        fontWeight: isToday ? 700 : 400,
                        fontSize: 10,
                      }}
                    >
                      <div>{d.toLocaleString("en", { weekday: "narrow" })}</div>
                      <div>{d.getDate()}</div>
                    </Box>
                  );
                })}
                <Box
                  component="th"
                  sx={{
                    px: 1,
                    textAlign: "right",
                    bgcolor: NAVY,
                    color: "#90caf9",
                    whiteSpace: "nowrap",
                    fontSize: 10,
                  }}
                >
                  Days
                </Box>
                <Box
                  component="th"
                  sx={{
                    px: 1,
                    textAlign: "right",
                    bgcolor: NAVY,
                    color: "#90caf9",
                    whiteSpace: "nowrap",
                    fontSize: 10,
                  }}
                >
                  Cost ($)
                </Box>
              </Box>
              {/* Col totals */}
              <Box component="tr" sx={{ bgcolor: "#f0f4fa" }}>
                <Box
                  component="td"
                  sx={{
                    px: 2,
                    py: 0.5,
                    fontWeight: 600,
                    fontSize: 11,
                    position: "sticky",
                    left: 0,
                    bgcolor: "#f0f4fa",
                    zIndex: 1,
                  }}
                >
                  Present
                </Box>
                {days.map((d) => {
                  const ds = fmtDate(d);
                  const cnt = colTotals[ds] ?? 0;
                  return (
                    <Box
                      key={ds}
                      component="td"
                      sx={{
                        textAlign: "center",
                        py: 0.5,
                        fontWeight: cnt > 0 ? 700 : 400,
                        color: cnt > 0 ? NAVY : "#bbb",
                      }}
                    >
                      {cnt > 0 ? cnt : ""}
                    </Box>
                  );
                })}
                <Box component="td" />
                <Box component="td" />
              </Box>
            </Box>

            {/* BODY */}
            <Box component="tbody">
              {groups.map(({ block, rows }) => (
                <React.Fragment key={block?.id ?? "__none"}>
                  {/* Block header row */}
                  <Box component="tr">
                    <Box
                      component="td"
                      colSpan={days.length + 3}
                      sx={{
                        px: 2,
                        py: 1,
                        bgcolor: "#e8f0fa",
                        color: NAVY,
                        fontWeight: 700,
                        fontSize: 11,
                        letterSpacing: 0.5,
                        textTransform: "uppercase",
                        borderTop: "2px solid #c5d8f5",
                        position: "sticky",
                        left: 0,
                      }}
                    >
                      {block?.name ?? "Unassigned"} — {rows.length}{" "}
                      {rows.length === 1 ? "worker" : "workers"}
                    </Box>
                  </Box>

                  {rows.map((w, ri) => {
                    const totals = rowTotals[w.id!] ?? { days: 0, cost: 0 };
                    return (
                      <Box
                        component="tr"
                        key={w.id}
                        sx={{
                          bgcolor: ri % 2 === 0 ? "#fff" : "#fafbfc",
                          "&:hover": { bgcolor: "#f0f6ff" },
                        }}
                      >
                        {/* Worker name cell */}
                        <Box
                          component="td"
                          sx={{
                            px: 2,
                            py: 0.5,
                            position: "sticky",
                            left: 0,
                            zIndex: 1,
                            bgcolor: "inherit",
                            whiteSpace: "nowrap",
                            borderRight: "2px solid #e8eaed",
                          }}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            gap={1}
                          >
                            <Box>
                              <Typography
                                variant="body2"
                                fontWeight={500}
                                fontSize={12}
                              >
                                {w.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontSize={10}
                              >
                                {w.role}
                              </Typography>
                            </Box>
                            <Box display="flex" gap={0.3}>
                              <Tooltip title="Mark all present">
                                <Button
                                  size="small"
                                  sx={{
                                    minWidth: 20,
                                    p: 0.2,
                                    fontSize: 9,
                                    color: "#2e7d32",
                                  }}
                                  onClick={() =>
                                    markAllPresent(w.id!, w.defaultBlockId)
                                  }
                                >
                                  ✓✓
                                </Button>
                              </Tooltip>
                              <Tooltip title="Mark all absent">
                                <Button
                                  size="small"
                                  sx={{
                                    minWidth: 20,
                                    p: 0.2,
                                    fontSize: 9,
                                    color: "#c62828",
                                  }}
                                  onClick={() => markAllAbsent(w.id!)}
                                >
                                  ✗✗
                                </Button>
                              </Tooltip>
                            </Box>
                          </Box>
                        </Box>

                        {/* Attendance cells */}
                        {days.map((d) => {
                          const ds = fmtDate(d);
                          const cell = getCell(w.id!, ds);
                          const isPresent = cell.status === "present";
                          const isDirty = dirty.has(`${w.id}|${ds}`);
                          const isToday = ds === today;
                          const isWknd = d.getDay() === 0 || d.getDay() === 6;
                          const cellBlockName = cell.blockId
                            ? blockMap.get(cell.blockId)?.name
                            : undefined;
                          return (
                            <Tooltip
                              key={ds}
                              title={
                                isPresent && cellBlockName
                                  ? `${cellBlockName} — right-click to reassign`
                                  : isPresent
                                  ? "Right-click to assign block"
                                  : ""
                              }
                              disableInteractive
                            >
                              <Box
                                component="td"
                                onClick={() =>
                                  toggleCell(w.id!, ds, w.defaultBlockId)
                                }
                                onContextMenu={(e) => {
                                  e.preventDefault();
                                  setCellBlockEdit({ wid: w.id!, ds });
                                }}
                                sx={{
                                  width: CELL_SIZE,
                                  minWidth: CELL_SIZE,
                                  textAlign: "center",
                                  cursor: "pointer",
                                  userSelect: "none",
                                  py: 0.5,
                                  bgcolor: isPresent
                                    ? isDirty
                                      ? "#c8f5c8"
                                      : "#e8f5e9"
                                    : isToday
                                    ? "#fff8e1"
                                    : isWknd
                                    ? "#f5f7ff"
                                    : "transparent",
                                  color: isPresent ? "#1b5e20" : "#bbb",
                                  fontWeight: isPresent ? 700 : 400,
                                  fontSize: 14,
                                  border: isToday
                                    ? "1px solid #ffd54f"
                                    : "1px solid transparent",
                                  "&:hover": {
                                    bgcolor: isPresent ? "#a5d6a7" : "#e3f2fd",
                                  },
                                  transition: "background-color 0.1s",
                                }}
                              >
                                {isPresent ? "✓" : ""}
                              </Box>
                            </Tooltip>
                          );
                        })}

                        {/* Row totals */}
                        <Box
                          component="td"
                          sx={{
                            textAlign: "right",
                            px: 1,
                            fontWeight: totals.days > 0 ? 600 : 400,
                            color: totals.days > 0 ? NAVY : "#bbb",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {totals.days > 0 ? totals.days : "—"}
                        </Box>
                        <Box
                          component="td"
                          sx={{
                            textAlign: "right",
                            px: 1,
                            fontWeight: totals.cost > 0 ? 600 : 400,
                            color: totals.cost > 0 ? "#1b5e20" : "#bbb",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {totals.cost > 0 ? `$${totals.cost}` : "—"}
                        </Box>
                      </Box>
                    );
                  })}
                </React.Fragment>
              ))}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Cell block override popover */}
      {cellBlockEdit && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(0,0,0,0.3)",
          }}
          onClick={() => setCellBlockEdit(null)}
        >
          <Card
            sx={{ minWidth: 300, p: 2 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography variant="subtitle1" fontWeight={600} mb={2}>
              Override block for this day
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Block</InputLabel>
              <Select
                label="Block"
                value={
                  grid[cellBlockEdit.wid]?.[cellBlockEdit.ds]?.blockId ?? ""
                }
                onChange={(e) =>
                  setCellBlock(
                    cellBlockEdit.wid,
                    cellBlockEdit.ds,
                    e.target.value,
                  )
                }
              >
                {blocks.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button onClick={() => setCellBlockEdit(null)} sx={{ mt: 2 }}>
              Cancel
            </Button>
          </Card>
        </Box>
      )}
    </Box>
  );
};
